import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button, { ButtonTypes } from '../../../../components/button';
import EntryDetailFooter from '../../../../components/entryDetail/entryDetailFooter';
import EntryDetailHeader from '../../../../components/entryDetail/entryDetailHeader';
import JurorEvaluation from '../../../../components/entryDetail/entryEvaluation';
import EntryNavigation from '../../../../components/entryDetail/entryNavigation';
import Loading from '../../../../components/loading';
import { ROLES } from '../../../../config/permissions/roles';
import { isAwardType } from '../../../../helpers/awardTypes';
import { useJuror } from '../../../../hooks/useAuth';
import { AWARD_TYPES } from '../../../../models/award';
import { SESSION_TYPES } from '../../../../models/session';
import Submission from '../../../../models/submission';
import styles from './styles.module.scss';

const EntryDetail = () => {
  const { entryId, jurorId: impersonatingJurorId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { juror, jurorGroup, session, role, isPreview } = useJuror();
  const [entryLoading, setEntryLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [juryStatement, setJuryStatement] = useState('');
  const [showJurorStatement, setShowJurorStatement] = useState(false);

  const jurorGroupId = state?.jurorGroupId ?? jurorGroup?.jurorGroupId;

  useEffect(() => {
    if (entryId && juror && jurorGroup && session) {
      updateEntry();
    }
  }, [entryId, juror, jurorGroup, impersonatingJurorId, session]);

  const updateEntry = async () => {
    setEntryLoading(true);

    let data = await Submission.fetch(
      jurorGroupId,
      entryId,
      impersonatingJurorId
    );

    if (!data) {
      return navigate(`/juror/entries`);
    }

    if (
      isAwardType(AWARD_TYPES.IF_DA, session) &&
      session?.sessionType === SESSION_TYPES.FINAL_JURY &&
      role === ROLES.chairPerson
    ) {
      const allEvaluations = await Submission.getEvaluations(entryId);
      await Promise.all(
        allEvaluations.map((evaluation) =>
          Submission.fetch(jurorGroupId, entryId, evaluation.jurorId)
        )
      ).then((values) => {
        const hasSuggestion = values.some(
          (submission) => submission.suggestedForNomination
        );
        data = { ...data, suggestedForNominationExternal: hasSuggestion };
      });
    }

    const statement = await Submission.fetchJuryStatement(
      jurorGroupId,
      entryId
    );
    setJuryStatement(statement || '');

    setSubmission(data);
    setEntryLoading(false);
  };

  const handleSubmissionUpdate = async (updatedSubmission) => {
    if (
      submission.suggestedForNomination !==
      updatedSubmission.suggestedForNomination
    ) {
      if (updatedSubmission.suggestedForNomination) {
        const statement = await Submission.fetchJuryStatement(
          jurorGroup.jurorGroupId,
          entryId
        );
        setJuryStatement(statement);
      } else {
        setJuryStatement('');
      }
    }

    setSubmission({
      ...updatedSubmission,
    });
  };

  const handleEvaluationChange = (points, criterionId) => {
    setSubmission((prev) => {
      return {
        ...prev,
        points: prev.points.map((item) => {
          if (item.criterion.id === criterionId) {
            return {
              ...item,
              points,
            };
          }
          return item;
        }),
      };
    });
  };

  const onJurorStatementChange = async (statement) => {
    if (statement) {
      await Submission.setJuryStatement({
        juryStatement: statement,
        jurorGroupId: jurorGroup?.jurorGroupId,
        submissionId: entryId,
      });
    } else {
      await Submission.deleteJuryStatement({
        jurorGroupId: jurorGroup?.jurorGroupId,
        submissionId: entryId,
      });
    }
  };

  const debounceFn = useCallback(debounce(onJurorStatementChange, 300), []);

  function handleJurorStatementChange(event) {
    setJuryStatement(event?.target?.value);
    debounceFn(event.target.value);
  }

  return (
    <div className={styles.entry}>
      <EntryNavigation
        entryLoading={entryLoading}
        submission={submission}
        submissionsList={state?.submissionsList}
      />
      {entryLoading ? (
        <div className={styles.loadingContainer}>
          <Loading />
        </div>
      ) : (
        <>
          {isAwardType(AWARD_TYPES.IF_DA, session) &&
            session.sessionType == SESSION_TYPES.FINAL_JURY &&
            submission?.nominated && (
              <Container className={styles.goldAwardedContainer}>
                <Row>
                  <Col>
                    <h1>{t('evaluation.goldDecided')}</h1>
                    {!juryStatement && (
                      <Button
                        className={styles.juryStatementButton}
                        type={ButtonTypes.SECONDARY}
                        onClick={() => setShowJurorStatement(true)}
                        icon={faArrowRight}
                        iconBold={faArrowRight}
                        text={t('evaluation.writeAJuryStatement')}
                        htmlType="button"
                      />
                    )}
                  </Col>
                </Row>
              </Container>
            )}
          {isAwardType(AWARD_TYPES.IF_DA, session) &&
            session.sessionType === SESSION_TYPES.FINAL_JURY &&
            (juryStatement || showJurorStatement) && (
              <div className={styles.juryStatementWrapper}>
                <Container>
                  <Row>
                    <Col className={styles.juryStatement}>
                      <h3>{t('evaluation.juryStatement')}</h3>
                      <textarea
                        readOnly={
                          session?.sessionType !== SESSION_TYPES.FINAL_JURY ||
                          isPreview
                        }
                        onChange={handleJurorStatementChange}
                      >
                        {juryStatement}
                      </textarea>
                    </Col>
                  </Row>
                </Container>
              </div>
            )}
          <div className={styles.contentWrapper}>
            <EntryDetailHeader
              submission={submission}
              handleSubmissionUpdate={handleSubmissionUpdate}
            />
          </div>
          <JurorEvaluation
            onEvaluationChange={handleEvaluationChange}
            submission={submission}
            updateSubmissions={handleSubmissionUpdate}
            isReadOnly={
              state?.jurorGroupId &&
              state.jurorGroupId !== jurorGroup?.jurorGroupId
            }
          />
          <div className={styles.contentWrapper}>
            <EntryDetailFooter submission={submission} />
          </div>
          <EntryNavigation
            entryLoading={entryLoading}
            submission={submission}
            submissionsList={state?.submissionsList}
          />
        </>
      )}
    </div>
  );
};

export default EntryDetail;
